/**
 * Accueil counters — the `WS2_EA_CTR_*` endpoints (contract §6), each
 * returning a single row `{ NB }` (`data[0].NB` of the VEOS list envelope):
 *
 * | tile        | ERP                             | slug                                 | fiche          |
 * |-------------|---------------------------------|--------------------------------------|----------------|
 * | `contrats`  | `WS2_EA_CTR_POLICES_ACTIVES`    | `boby-ws2-ea-ctr-polices-actives`    | 22062 / 22058  |
 * | `sinistres` | `WS2_EA_CTR_SINISTRES_EN_COURS` | `boby-ws2-ea-ctr-sinistres-en-cours` | 22067          |
 * | `primes`    | `WS2_EA_CTR_PRIMES_IMPAYEES`    | `boby-ws2-ea-ctr-primes-impayees`    | 22065          |
 * | `vehicules` | `WS2_EA_CTR_VEHICULES_ACTIFS`   | `boby-ws2-ea-ctr-vehicules-actifs`   | 22079          |
 *
 * No caller parameter: `SES` is appended by the proxy from the
 * `X-Erp-Session-Id` header. The pending-pieces counter (`WS2_EA_CTR_PMQ`)
 * is NOT here — it is shared with the header bell through
 * `hooks/usePendingPiecesCount`.
 */

import { toFiniteNumber, type VeosScalar } from '../utils/format';
import { fetchErpRecord } from './erpProxy';

/** Endpoint slug of each Accueil counter, keyed by the tile's stable key. */
export const HOME_COUNTER_ENDPOINTS = {
  contrats: 'boby-ws2-ea-ctr-polices-actives',
  sinistres: 'boby-ws2-ea-ctr-sinistres-en-cours',
  primes: 'boby-ws2-ea-ctr-primes-impayees',
  vehicules: 'boby-ws2-ea-ctr-vehicules-actifs',
} as const;

/** Stable key of an Accueil counter. */
export type HomeCounterKey = keyof typeof HOME_COUNTER_ENDPOINTS;

/** Endpoint slug of an Accueil counter. */
export type HomeCounterEndpoint = (typeof HOME_COUNTER_ENDPOINTS)[HomeCounterKey];

/** The single row of a `WS2_EA_CTR_*` counter endpoint. */
export interface HomeCounterRow {
  NB: VeosScalar;
}

/**
 * Read the counter off a `WS2_EA_CTR_*` row: `NB` as a number (numeric
 * strings accepted); `0` when the row or `NB` is missing, non-numeric or
 * negative.
 */
export function counterOf(row: HomeCounterRow | null | undefined): number {
  const count = toFiniteNumber(row?.NB);
  return count === null || count < 0 ? 0 : count;
}

/**
 * Fetch one Accueil counter (`data[0].NB`) on the active VEOS registry.
 *
 * @throws Error with the ERP message on any proxy / VEOS failure, or when no
 *   registry is active.
 */
export async function fetchCounter(
  registrySlug: string | null | undefined,
  endpointSlug: HomeCounterEndpoint,
): Promise<number> {
  return counterOf(await fetchErpRecord<HomeCounterRow>(registrySlug, endpointSlug));
}

/**
 * React Query key of a counter — scoped by endpoint, VEOS registry and
 * session (a SUPERVUE session switch must not reuse another client's count).
 */
export function homeCounterQueryKey(
  endpointSlug: HomeCounterEndpoint,
  registrySlug: string | null,
  sessionId: number | null,
): readonly unknown[] {
  return ['extranet', 'home-counter', endpointSlug, registrySlug, sessionId];
}
